import { CTM_DELIVERY_METHODS, CTM_PURPOSES, type CtmStatus } from '@felix/domain';
import { Button, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconCircleCheck,
  IconClock,
  IconFileText,
  IconPlus,
  IconStack2,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import { ReturnedBanner } from '../returned/Returned.js';
import classes from './Cash.module.css';
import type { CashData, CashSection, CtmItem } from './types.js';
import {
  amountOf,
  amountText,
  cashCurrencyChoices,
  ctmQueue,
  ctmStatusTone,
  formatMoney,
  formatWhole,
} from './view.js';

/**
 * Ortağın üst sırası (kasa-ekran.jpeg 1–3): CTM formu, onay ve durum zaman çizgisi, talep listesi
 * ve özet kartları. Gemide form yazılır; ofiste salt okunur, karar ve ödeme düzenlemesi zaman
 * çizgisindeki düğmelerle.
 */

export function Panel({
  index,
  title,
  actions,
  children,
  foot,
  label,
}: {
  index?: number;
  title: string;
  actions?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  label?: string;
}) {
  return (
    <section className={classes.panel} aria-label={label ?? title}>
      <header className={classes.panelHead}>
        <h2 className={classes.panelTitle}>
          {index ? `${index}. ` : ''}
          {title}
        </h2>
        {actions}
      </header>
      <div className={classes.panelBody}>{children}</div>
      {foot ? <div className={classes.panelFoot}>{foot}</div> : null}
    </section>
  );
}

export interface CtmFormValue {
  port: string;
  requiredDate: string | null;
  currency: string;
  requestedAmount: string;
  estimatedExpenses: string;
  purpose: string | null;
  deliveryMethod: string;
  remarks: string;
}

export function formOf(item: CtmItem | null, language: string): CtmFormValue {
  return {
    port: item?.port ?? '',
    requiredDate: item?.requiredDate ?? null,
    currency: item?.currency ?? 'USD',
    requestedAmount: amountText(item?.requestedAmount, language),
    estimatedExpenses: amountText(item?.estimatedExpenses, language),
    purpose: item?.purpose ?? null,
    deliveryMethod: item?.deliveryMethod ?? 'agent',
    remarks: item?.remarks ?? '',
  };
}

/** Formdan gemi sunucusunun beklediği girdi; tutar geçersizse `null`. */
export function inputOf(form: CtmFormValue, language: string): Record<string, unknown> | null {
  const requested = amountOf(form.requestedAmount, language);
  const estimated = form.estimatedExpenses.trim()
    ? amountOf(form.estimatedExpenses, language)
    : null;
  if (requested === null || (form.estimatedExpenses.trim() && estimated === null)) return null;
  return {
    port: form.port,
    requiredDate: form.requiredDate,
    currency: form.currency,
    requestedAmount: requested,
    estimatedExpenses: estimated,
    purpose: form.purpose,
    deliveryMethod: form.deliveryMethod,
    remarks: form.remarks.trim() || null,
  };
}

const EDITABLE: readonly CtmStatus[] = ['draft', 'returned'];

/** 1. CTM Request: gemide taslak ve iade edilmiş talep düzenlenir; başka durumda salt okunur. */
export function CtmRequestPanel(props: {
  data: CashData;
  item: CtmItem | null;
  creating: boolean;
  editable: boolean;
  busy: boolean;
  onSave: (form: CtmFormValue, submit: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const { data, item } = props;
  const [form, setForm] = useState<CtmFormValue>(() => {
    const value = formOf(item, language);
    // Yeni talep ofisin açık kasasında (ADR 0110 §1)
    const choices = cashCurrencyChoices(data, 'in');
    return props.creating && !choices.includes(value.currency) && choices[0]
      ? { ...value, currency: choices[0] }
      : value;
  });
  const canEdit =
    props.editable && (props.creating || (item ? EDITABLE.includes(item.status) : false));
  const set =
    <K extends keyof CtmFormValue>(key: K) =>
    (value: CtmFormValue[K]) =>
      setForm((f) => ({ ...f, [key]: value }));
  const balance =
    data.balances.find((b) => b.currency === form.currency)?.balance ??
    (item && !props.creating ? item.cashBalance : 0);
  const valid =
    inputOf(form, language) !== null &&
    form.port.trim() !== '' &&
    form.requiredDate !== null &&
    form.purpose !== null &&
    (form.purpose !== 'other' || form.remarks.trim() !== '');
  const f = (key: string) => t(`cash.ctm.fields.${key}`);
  const read = (value: ReactNode, label: string) => (
    <div>
      <span className={classes.fieldLabel}>{label}</span>
      <div className={classes.readValue}>{value}</div>
    </div>
  );
  if (!props.creating && !item) {
    return (
      <Panel index={1} title={t('cash.panels.request')}>
        <p className={classes.note}>{t('cash.ctm.selectHint')}</p>
      </Panel>
    );
  }
  return (
    <Panel
      index={1}
      title={t('cash.panels.request')}
      foot={
        canEdit ? (
          <>
            <Button
              variant="default"
              disabled={!valid || props.busy}
              onClick={() => props.onSave(form, false)}
            >
              {t('cash.ctm.actions.saveDraft')}
            </Button>
            <Button disabled={!valid || props.busy} onClick={() => props.onSave(form, true)}>
              {t('cash.ctm.actions.submit')}
            </Button>
          </>
        ) : undefined
      }
    >
      {/* Ofisin iadesi (ADR 0116 Ek): iade eden, zaman, gerekçe; eski sunucuda yalnız gerekçe */}
      {item?.status === 'returned' && item.returnNotice ? (
        <ReturnedBanner notice={item.returnNotice} hint={canEdit} />
      ) : item?.status === 'returned' && item.review?.reason ? (
        <p className={classes.notice} data-tone="danger">
          {t('cash.ctm.returnedNote', { reason: item.review.reason })}
        </p>
      ) : null}
      {item?.status === 'cancelled' && item.cancelReason ? (
        <p className={classes.note}>{t('cash.ctm.cancelledNote', { reason: item.cancelReason })}</p>
      ) : null}
      <div className={classes.formGrid}>
        {read(data.vessel.name, f('vessel'))}
        {read(item && !props.creating ? item.ctmNo : f('auto'), f('ctmNo'))}
        {read(formatDay(item && !props.creating ? item.requestDate : data.today), f('requestDate'))}
        {canEdit ? (
          <DayInput
            label={f('requiredDate')}
            required
            value={form.requiredDate}
            minDate={data.today}
            onChange={set('requiredDate')}
          />
        ) : (
          read(formatDay(form.requiredDate), f('requiredDate'))
        )}
        {canEdit ? (
          <TextInput
            className={classes.wide}
            label={f('port')}
            required
            maxLength={200}
            value={form.port}
            onChange={(e) => set('port')(e.currentTarget.value)}
          />
        ) : (
          <div className={classes.wide}>{read(form.port, f('port'))}</div>
        )}
        {canEdit ? (
          <Select
            label={f('currency')}
            data={cashCurrencyChoices(data, 'in')}
            value={form.currency}
            searchable
            allowDeselect={false}
            onChange={(v) => v && set('currency')(v)}
          />
        ) : (
          read(form.currency, f('currency'))
        )}
        {canEdit ? (
          <TextInput
            label={f('requestedAmount')}
            required
            inputMode="decimal"
            value={form.requestedAmount}
            onChange={(e) => set('requestedAmount')(e.currentTarget.value)}
          />
        ) : (
          read(formatMoney(item?.requestedAmount ?? 0, language), f('requestedAmount'))
        )}
        {read(`${form.currency} ${formatMoney(balance, language)}`, f('cashBalance'))}
        {canEdit ? (
          <TextInput
            label={f('estimatedExpenses')}
            inputMode="decimal"
            value={form.estimatedExpenses}
            onChange={(e) => set('estimatedExpenses')(e.currentTarget.value)}
          />
        ) : (
          read(
            item?.estimatedExpenses === null || item?.estimatedExpenses === undefined
              ? '—'
              : formatMoney(item.estimatedExpenses, language),
            f('estimatedExpenses'),
          )
        )}
        {canEdit ? (
          <Select
            label={f('purpose')}
            required
            data={CTM_PURPOSES.map((p) => ({ value: p, label: t(`cash.ctm.purposes.${p}`) }))}
            value={form.purpose}
            onChange={set('purpose')}
          />
        ) : (
          read(form.purpose ? t(`cash.ctm.purposes.${form.purpose}`) : '—', f('purpose'))
        )}
        {canEdit ? (
          <Select
            label={f('deliveryMethod')}
            data={CTM_DELIVERY_METHODS.map((m) => ({
              value: m,
              label: t(`cash.ctm.methods.${m}`),
            }))}
            value={form.deliveryMethod}
            allowDeselect={false}
            onChange={(v) => v && set('deliveryMethod')(v)}
          />
        ) : (
          read(t(`cash.ctm.methods.${form.deliveryMethod}`), f('deliveryMethod'))
        )}
        {canEdit ? (
          <Textarea
            className={classes.wide}
            label={f('remarks')}
            placeholder={f('remarksPlaceholder')}
            required={form.purpose === 'other'}
            autosize
            minRows={2}
            maxLength={2000}
            value={form.remarks}
            onChange={(e) => set('remarks')(e.currentTarget.value)}
          />
        ) : (
          <div className={classes.wide}>{read(form.remarks || '—', f('remarks'))}</div>
        )}
      </div>
      {item && !props.creating && !EDITABLE.includes(item.status) && props.editable ? (
        <p className={classes.note}>{t('cash.ctm.lockedNote')}</p>
      ) : null}
    </Panel>
  );
}

/** 2. CTM Approval & Status: beş adımlı zaman çizgisi ve adımın düğmesi. */
export function CtmStatusPanel(props: {
  item: CtmItem | null;
  actions: Partial<Record<'review' | 'arranged' | 'received' | 'created', ReactNode>>;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [tab, setTab] = useState<'workflow' | 'history'>('workflow');
  const { item } = props;
  return (
    <Panel index={2} title={t('cash.panels.status')}>
      <div className={classes.tabs} role="tablist">
        {(['workflow', 'history'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            className={classes.tab}
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {t(`cash.tabs.${key}`)}
          </button>
        ))}
      </div>
      {!item ? (
        <p className={classes.note}>{t('cash.ctm.selectHint')}</p>
      ) : tab === 'history' ? (
        <dl className={classes.statement}>
          {(
            [
              [t('cash.ctm.fields.ctmNo'), item.ctmNo],
              [t('cash.ctm.fields.requestedBy'), item.requestedBy.name],
              [
                t('cash.ctm.fields.requestedAmount'),
                `${item.currency} ${formatMoney(item.requestedAmount, language)}`,
              ],
              [t('cash.ctm.fields.status'), t(`cash.ctm.statuses.${item.status}`)],
              [
                t('cash.ctm.steps.arranged'),
                item.transfer
                  ? `${item.currency} ${formatMoney(item.transfer.amount, language)}`
                  : '—',
              ],
              ...(item.transfer && item.transfer.amount !== item.requestedAmount
                ? ([
                    [
                      t('cash.decision.difference'),
                      `${item.currency} ${formatMoney(item.transfer.amount - item.requestedAmount, language)}`,
                    ],
                    [t('cash.decision.varianceReason'), item.transfer.varianceReason ?? '—'],
                  ] as const)
                : []),
              [
                t('cash.ctm.steps.received'),
                item.receipt
                  ? `${item.currency} ${formatMoney(item.receipt.amount, language)} · ${item.receipt.txnNo}`
                  : '—',
              ],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className={classes.statementRow}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <ol className={classes.timeline}>
          {item.timeline.map((step, index) => {
            const done = step.state === 'done';
            const badge =
              step.state === 'done'
                ? t(
                    step.key === 'review' || step.key === 'arranged'
                      ? `cash.ctm.stepDone.${step.key}`
                      : 'cash.ctm.stepStates.done',
                  )
                : t(`cash.ctm.stepStates.${step.state}`);
            const tone =
              step.state === 'done'
                ? 'success'
                : step.state === 'current'
                  ? 'info'
                  : step.state === 'returned' || step.state === 'cancelled'
                    ? 'danger'
                    : 'neutral';
            const action = props.actions[step.key as keyof typeof props.actions];
            return (
              <li key={step.key} className={classes.step} data-state={step.state}>
                <span className={classes.dot} aria-hidden>
                  {done ? <IconCircleCheck size={18} /> : index + 1}
                </span>
                <div>
                  <p className={classes.stepName}>{t(`cash.ctm.steps.${step.key}`)}</p>
                  {step.at && step.state !== 'waiting' ? (
                    <span className={classes.stepMeta}>{formatDateTime(step.at)}</span>
                  ) : null}
                  {step.by ? (
                    <span className={classes.stepMeta}>{t('cash.ctm.by', { name: step.by })}</span>
                  ) : null}
                  {step.note ? (
                    <span className={classes.stepMeta}>
                      {step.key === 'arranged'
                        ? t('cash.ctm.via', { text: methodNote(step.note, t) })
                        : step.note}
                    </span>
                  ) : null}
                  {step.key === 'arranged' &&
                  item.transfer &&
                  item.transfer.amount !== item.requestedAmount ? (
                    <span
                      className={classes.stepMeta}
                      data-tone="warning"
                      title={item.transfer.varianceReason ?? undefined}
                    >
                      {t('cash.ctm.sentOf', {
                        sent: `${item.currency} ${formatMoney(item.transfer.amount, language)}`,
                        requested: formatMoney(item.requestedAmount, language),
                      })}
                    </span>
                  ) : null}
                  {step.key === 'posted' && !done ? (
                    <span className={classes.stepMeta}>{t('cash.ctm.postedNote')}</span>
                  ) : null}
                </div>
                <StatusBadge tone={tone}>{badge}</StatusBadge>
                {action ? <div className={classes.stepAction}>{action}</div> : null}
              </li>
            );
          })}
        </ol>
      )}
      <p className={classes.rule}>{t('cash.ctm.criticalRule')}</p>
    </Panel>
  );
}

/** "agent · Antibes" → "Agent – Antibes". */
function methodNote(note: string, t: (key: string) => string): string {
  const [method, ...rest] = note.split(' · ');
  const label = method ? t(`cash.ctm.methods.${method}`) : '';
  return [label, ...rest].filter(Boolean).join(' – ');
}

const SUMMARY = [
  { key: 'requested', icon: IconFileText },
  { key: 'approved', icon: IconCircleCheck },
  { key: 'received', icon: IconStack2 },
  { key: 'pending', icon: IconClock },
] as const;

/** 3. CTM Request List ve CTM Summary (para birimine göre; kur çevirisi yok). */
export function CtmListPanel(props: {
  data: CashData;
  section: CashSection;
  selectedId: string | null;
  canCreate: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [status, setStatus] = useState<string>('all');
  const queue = ctmQueue(props.data.ctm.items, props.section).filter(
    (i) => status === 'all' || i.status === status,
  );
  const statuses = [...new Set(props.data.ctm.items.map((i) => i.status))];
  const summary = props.data.ctm.summary;
  return (
    <div className={classes.stack}>
      <Panel index={3} title={t('cash.panels.list')}>
        <div className={classes.toolbar}>
          <Select
            label={t('cash.ctm.statusFilter')}
            data={[
              { value: 'all', label: t('cash.all') },
              ...statuses.map((s) => ({ value: s, label: t(`cash.ctm.statuses.${s}`) })),
            ]}
            value={status}
            allowDeselect={false}
            onChange={(v) => setStatus(v ?? 'all')}
            w={170}
          />
          {props.canCreate ? (
            <div className={classes.toolbarEnd}>
              <Button leftSection={<IconPlus size={16} />} onClick={props.onNew}>
                {t('cash.ctm.actions.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {queue.length === 0 ? (
          <p className={classes.note}>
            {props.data.ctm.items.length === 0 ? t('cash.ctm.empty') : t('cash.ctm.emptyQueue')}
          </p>
        ) : (
          <section
            className={classes.tableWrap}
            aria-label={t('cash.tableOf', { name: t('cash.panels.list') })}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table} data-compact>
              <thead>
                <tr>
                  <th scope="col">{t('cash.ctm.columns.ctmNo')}</th>
                  <th scope="col">{t('cash.ctm.columns.date')}</th>
                  <th scope="col">{t('cash.ctm.columns.port')}</th>
                  <th scope="col">{t('cash.ctm.columns.currency')}</th>
                  <th scope="col" className={classes.numeric}>
                    {t('cash.ctm.columns.amount')}
                  </th>
                  <th scope="col">{t('cash.ctm.columns.purpose')}</th>
                  <th scope="col">{t('cash.ctm.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item) => (
                  <tr key={item.id} data-selected={item.id === props.selectedId || undefined}>
                    <td className={classes.nowrap}>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => props.onSelect(item.id)}
                      >
                        {item.ctmNo}
                      </button>
                    </td>
                    <td className={classes.nowrap}>{formatDay(item.requestDate)}</td>
                    <td>{item.port.split(',')[0]}</td>
                    <td>{item.currency}</td>
                    <td className={classes.numeric}>
                      {formatMoney(item.requestedAmount, language)}
                    </td>
                    <td>{t(`cash.ctm.purposes.${item.purpose}`)}</td>
                    <td>
                      <StatusBadge tone={ctmStatusTone(item.status)}>
                        {t(`cash.ctm.statuses.${item.status}`)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </Panel>
      <section className={classes.panel} aria-label={t('cash.panels.summary')}>
        <div className={classes.panelBody}>
          <h2 className={classes.subTitle}>{t('cash.panels.summary')}</h2>
          <div className={classes.summaryCards}>
            {SUMMARY.map(({ key, icon: Icon }) => (
              <div key={key} className={classes.summaryCard} data-tone={key}>
                <span className={classes.summaryLabel}>{t(`cash.ctm.summary.${key}`)}</span>
                <div className={classes.summaryValue}>
                  <span className={classes.summaryIcon} aria-hidden>
                    <Icon size={20} />
                  </span>
                  <span className={classes.summaryAmounts}>
                    {summary.length === 0 ? (
                      <span className={classes.summaryAmount}>0</span>
                    ) : (
                      summary.map((row) => (
                        <span key={row.currency} className={classes.summaryAmount}>
                          {row.currency} {formatWhole(row[key], language)}
                        </span>
                      ))
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
